import React, { useState } from 'react';
import { GameCartridge, Archetype, Companion } from '@/types';
import { databaseService } from '@/services/databaseService';
import { ARCHETYPES, INITIAL_COMPANIONS } from '@/constants';
import { v4 as uuidv4 } from 'uuid';

interface CartridgeEditorScreenProps {
  onBack?: () => void;
  onCartridgeCreated?: (cartridge: GameCartridge) => void;
}

const CartridgeEditorScreen: React.FC<CartridgeEditorScreenProps> = ({ onBack, onCartridgeCreated }) => {
  // Provide default archetypes and companions
  const defaultArchetypes: Archetype[] = ARCHETYPES;
  const defaultCompanions: Companion[] = INITIAL_COMPANIONS;
  
  const [cartridge, setCartridge] = useState<Partial<GameCartridge>>({
    name: '',
    description: '',
    tags: [],
    isPublic: true,
    maxPlayers: 4,
    version: '1.0.0',
    archetypes: defaultArchetypes,
    companions: defaultCompanions,
    worldSettings: {
      timeperiod: '2077-2125',
      setting: 'Cyberpunk',
      themes: ['consciousness', 'identity', 'technology'],
      atmosphere: 'dark cyberpunk',
      initialLocation: 'Neo-Veridia Central District',
      imageStyle: 'Dark cyberpunk, neon aesthetics, atmospheric lighting'
    },
    storyPrompts: {
      systemPrompt: 'You are a master storyteller for a text-based RPG.',
      chatSystemPrompt: 'You are controlling AI companions and other characters.',
      initialNarrative: 'You are {playerName}, age {playerAge}, embarking on an adventure.',
      companionIntro: 'Your companions materialize beside you.',
      initialScene: 'You find yourself in the starting location.',
      initialImagePrompt: 'Atmospheric scene, detailed environment, cinematic lighting'
    },
    supportedFeatures: ['basic', 'multiplayer', 'images', 'saves', 'branching_story'],
    thumbnailUrl: undefined,
    isBuiltIn: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCartridge(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setCartridge(prev => ({ ...prev, [name]: checked }));
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCartridge(prev => ({ ...prev, tags: value.split(',').map(tag => tag.trim()) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Generate a unique ID and set timestamps
      const now = new Date().toISOString();
      const cartridgeWithDefaults: GameCartridge = {
        id: uuidv4(),
        author: 'User',
        authorId: 'user',
        createdAt: now,
        updatedAt: now,
        ...cartridge
      } as GameCartridge;
      
      const newCartridge = await databaseService.createUserCartridge(cartridgeWithDefaults);
      console.log('Cartridge created:', newCartridge);
      onCartridgeCreated?.(newCartridge);
      onBack?.(); // Navigate back to cartridge browser
    } catch (err) {
      console.error('Error creating cartridge:', err);
      setError(err instanceof Error ? err.message : 'Failed to create cartridge');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-orbitron text-cyan-400 neon-text-cyan mb-2">
          Cartridge Editor
        </h1>
        <p className="text-slate-400 mb-8">
          Create and edit your own game cartridges.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
              Cartridge Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={cartridge.name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-2">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={cartridge.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
              required
            />
          </div>

          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-slate-300 mb-2">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={cartridge.tags?.join(', ')}
              onChange={handleTagsChange}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
            />
          </div>

          <div className="flex items-center justify-between">
            <label htmlFor="isPublic" className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isPublic"
                name="isPublic"
                checked={cartridge.isPublic}
                onChange={handleCheckboxChange}
                className="h-4 w-4 rounded border-slate-500 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
              />
              <span className="text-sm text-slate-300">Publicly discoverable</span>
            </label>

            <div>
              <label htmlFor="maxPlayers" className="text-sm font-medium text-slate-300 mr-2">
                Max Players
              </label>
              <input
                type="number"
                id="maxPlayers"
                name="maxPlayers"
                min="1"
                max="16"
                value={cartridge.maxPlayers}
                onChange={handleInputChange}
                className="w-20 px-3 py-1 bg-slate-800 border border-slate-600 rounded-lg text-white"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-900/50 border border-red-500/50 rounded-lg text-red-200">
              <p className="font-semibold">Error:</p>
              <p>{error}</p>
            </div>
          )}

          <div className="pt-4 flex justify-end gap-4">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating...' : 'Create Cartridge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CartridgeEditorScreen; 